import type {
  ProductSubscriptionResponseDto,
  StorageUnitOrderResponseDto,
} from '@wanda-space/types'

// The API sets `warehouseInfo.location` when a storage unit order is fulfilled, but the
// @wanda-space/types version we depend on predates the field.
type WarehouseInfo = StorageUnitOrderResponseDto['warehouseInfo'] & {
  location?: { id: string; label: string }
}

export type StorageUnitWarehouseGroup = {
  // null when no storage unit order points at the subscription's location
  warehouse: Omit<WarehouseInfo, 'location'> | null
  storageUnits: {
    subscription: ProductSubscriptionResponseDto
    locationLabel?: string
  }[]
}

/**
 * Groups storage unit subscriptions (one per unit) by the warehouse they're in. A subscription
 * only knows its location, so the warehouse comes from the storage unit order that was fulfilled
 * into that location.
 */
export function groupStorageUnitSubscriptionsByWarehouse(
  subscriptions: ProductSubscriptionResponseDto[],
  storageUnitOrders: StorageUnitOrderResponseDto[]
): StorageUnitWarehouseGroup[] {
  const warehouseInfoByLocationId = new Map<string, WarehouseInfo>()
  for (const order of storageUnitOrders) {
    const warehouseInfo: WarehouseInfo = order.warehouseInfo
    if (warehouseInfo.location) {
      warehouseInfoByLocationId.set(warehouseInfo.location.id, warehouseInfo)
    }
  }

  const groups = new Map<string | null, StorageUnitWarehouseGroup>()
  for (const subscription of subscriptions) {
    const warehouseInfo = subscription.locationId
      ? warehouseInfoByLocationId.get(subscription.locationId)
      : undefined
    const key = warehouseInfo?.id ?? null

    let group = groups.get(key)
    if (!group) {
      const { id, name, address } = warehouseInfo ?? {}
      group = { warehouse: id && name && address ? { id, name, address } : null, storageUnits: [] }
      groups.set(key, group)
    }
    group.storageUnits.push({ subscription, locationLabel: warehouseInfo?.location?.label })
  }

  const byLabel = (a?: string, b?: string) =>
    (a ?? '').localeCompare(b ?? '', undefined, { numeric: true })

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      storageUnits: group.storageUnits.sort((a, b) => byLabel(a.locationLabel, b.locationLabel)),
    }))
    .sort((a, b) => {
      // Units we couldn't place go last
      if (!a.warehouse || !b.warehouse) {
        return a.warehouse ? -1 : b.warehouse ? 1 : 0
      }
      return byLabel(a.warehouse.name, b.warehouse.name)
    })
}
